/**
 * Il marchio Youppybuy.
 *
 * Trascrizione dello schizzo del titolare. **La faccia è «youppy»**: due occhi
 * sopra, e la parola che si incurva a fare la bocca. Da lì la riga torna
 * dritta — il **carrello al posto della «b»** e «uy» orizzontale, così che
 * «🛒uy» si legga *buy*. I tre segni si toccano, e alla stessa quota: carrello
 * e «uy» stanno all'altezza della «y» finale, non sulla riga di base teorica,
 * che la parabola lascia parecchio più in alto.
 *
 * ## L'entrata
 *
 * Con `animato`, il marchio si compone davanti a chi guarda: il carrello arriva
 * da lontano, piccolo, **in curva**, alzando sabbia dalle ruote mentre corre; le
 * lettere di «youppy» escono da sotto il cesto e sfilano a sinistra, distese;
 * «uy» sfila a destra; poi la parola si incurva a sorriso — **tutte insieme**,
 * perche' un sorriso si forma tutto insieme — gli occhi si aprono come palpebre
 * e il sinistro fa l'occhiolino.
 *
 * Ogni lettera sta in **due gruppi annidati**: fuori la curva, dentro l'entrata.
 * Non e' un vezzo — due animazioni sullo stesso elemento si contenderebbero la
 * `transform`, e le due fasi non potrebbero avere tempi propri.
 *
 * I tempi stanno tutti in `globals.css`, da qui passano solo le posizioni.
 *
 * Le palpebre non sono due rettangoli del colore di sfondo — funzionerebbero
 * solo sopra un fondo noto, e il marchio deve reggere su qualunque colore.
 * Sono un ritaglio che si apre dal centro: qualunque cosa ci sia dietro, resta.
 *
 * **Tutte le animazioni stanno dentro `prefers-reduced-motion`.** Il disegno
 * statico è già lo stato finale e le animazioni lo scostano soltanto: chi ha
 * chiesto meno movimento vede il logo finito, e se il foglio di stile non
 * arriva il peggio che succede è che stia fermo.
 *
 * ## Due registri
 *
 * `scomposto` (predefinito) è la versione con l'energia giusta per un'insegna:
 * lettere sfalsate, di corpo diverso e sovrapposte, carrello inclinato. In un
 * settore di marchi grigi tutti uguali, si ricorda.
 *
 * `scomposto={false}` toglie lo scarto e allinea tutto: serve dove il segno deve
 * **farsi prendere sul serio** — fattura, ordine al fornitore, carta intestata.
 * Il sito chiede un bonifico anticipato a chi non ci conosce, e quel documento
 * non è il posto per la faccia buffa.
 *
 * ## Il carattere
 *
 * Non è quello del sito. Geist è geometrico e pulito: per quanto si scomponga
 * non diventa disegnato a mano. Il marchio usa **Baloo 2** — grassa, tonda,
 * leggermente irregolare — caricata da `next/font`, che scarica il file in fase
 * di build e lo serve dal nostro dominio. Nessuna richiesta a Google quando
 * qualcuno apre il sito: è la stessa ragione per cui non serve un banner cookie.
 *
 * ## Tre cose che non si possono fare nel modo breve
 *
 * **La curva.** La via corta sarebbe un solo `<text>` con la lista `y` per
 * glifo. Non funziona: parecchi motori la ignorano, e in combinazione con
 * `textLength` la lasciano cadere anche i browser — la parola esce dritta e il
 * sorriso sparisce. Qui ogni lettera è un `<text>` con posizione e rotazione
 * proprie, via `transform`, che è supportata ovunque.
 *
 * **Lo scarto è scritto a mano, non casuale.** Deve essere identico a ogni
 * caricamento, e soprattutto identico fra server e browser: un `Math.random()`
 * qui produrrebbe un errore di idratazione a ogni visita.
 *
 * **La tela si misura, non si fissa.** Le lettere di corpo diverso allargano la
 * parola: con un `viewBox` fisso la «y» finale finisce tagliata, ed è successo.
 *
 * Il testo resta testo e non tracciati: resta nitido a ogni dimensione. In cambio va tenuto inline nel DOM — un `<img>`
 * verso un `.svg` non caricherebbe il webfont — e serve `textLength` per
 * bloccare l'avanzamento, altrimenti basta un ripiego più largo e il carrello
 * finisce sopra una lettera.
 */

/* ── Proporzioni. È qui che si correggono. ────────────────────────────────── */
const DIM = 46; //              corpo del carattere
const X0 = 10; //               dove comincia «youppy»
const BASE = 74; //             quota di partenza del sorriso
const PROFONDITA = 50; //       quanto affonda il sorriso al centro
/* Quanto ogni lettera si sovrappone alla successiva. Non e' un numero solo: la
   «y» ha le diagonali, e una diagonale lascia un vuoto ottico che una lettera
   tonda non ha. Alla stessa distanza sembra staccata, quindi va avvicinata di
   piu'. Cinque valori per i cinque intervalli di «youppy». */
const STRETTE = [9, 4, 4, 4, 9];
const STRETTA_UY = 9; //        stesso motivo, fra la «u» e la «y» di «uy»
/* Piu' in basso di quanto verrebbe naturale: con la conca profonda le lettere
   agli estremi risalgono parecchio, e occhi troppo alti si staccano dalla
   bocca invece di formarci una faccia. */
const OCCHI_Y = 31;
const OCCHI_R = 12;
const OCCHI_DIST = 36; //       distanza di ciascun occhio dal centro della bocca
const CARRELLO_STACCO = -14; // negativo: la «y» inclinata finisce prima del suo riquadro
const CESTO_LARG = 44;
const UY_STACCO = 0;
const UY_U_LARG = 36; //        «uy» sono due lettere posizionate, non un testo
const UY_Y_LARG = 31; //        solo: servirebbe altrimenti un unico textLength,
//                              e non si potrebbe stringere la coppia
const TRATTO = 7.5; //          spessore del carrello, in pari col peso delle lettere
const CARRELLO_DIM = 1.16; //   quanto e' grande il carrello. Un numero solo: il
//                              segno e' disegnato in coordinate proprie
const INCLINA_CARRELLO = -7;
const ALTEZZA = 152; //         il sorriso piu' profondo scende di piu'

/* Lo scarto, lettera per lettera. Fisso: vedi la nota sull'idratazione.

   E **simmetrico**, che e' la cosa che conta. La prima versione aveva valori
   scelti a occhio per il ritmo, e la prima «y» finiva tre unita' piu' in alto e
   l'ultima due piu' in basso: cinque unita' di dislivello fra i due estremi, e
   il sorriso pendeva da un lato. Un sorriso storto non si legge come scomposto,
   si legge come sbagliato.

   Quindi ogni lettera e' in pari con la sua speculare: la prima con la sesta,
   la seconda con la quinta, la terza con la quarta. Le rotazioni si specchiano
   di segno opposto, perche' i due lati della curva pendono in direzioni
   contrarie. L'ampiezza e' ridotta: la mano si vede lo stesso, la conca resta
   una conca. */
const SCARTO_Y = [-2, 1.5, -1, -1, 1.5, -2];
const SCARTO_ROT = [-2, 2, -1, 1, -2, 2];
const SCALA = [1.04, 1.0, 1.05, 1.05, 1.0, 1.04];

/** Larghezza di base di ogni lettera. Fissa di proposito: vedi `textLength`. */
// Baloo 2 e' piu' larga e piu' tonda di Geist: le lettere vogliono piu' spazio,
// che poi si riprende con la sovrapposizione.
const LARGHEZZA: Record<string, number> = { y: 31, o: 34, u: 36, p: 33 };
const NOME = [..."youppy"];

type Lettera = {
  carattere: string;
  x: number;
  y: number;
  cx: number;
  gradi: number;
  larghezza: number;
  corpo: number;
  chiave: string;
};

/** Le lettere del sorriso: una parabola simmetrica, più lo scarto se richiesto. */
function sorriso(scomposto: boolean) {
  const larghezze = NOME.map(
    (c, i) => (LARGHEZZA[c] ?? 26) * (scomposto ? SCALA[i] : 1),
  );
  const passi = larghezze.map((w, i) => w - (STRETTE[i] ?? 0));
  const totale = passi.slice(0, -1).reduce((a, b) => a + b, 0) + larghezze.at(-1)!;
  const centro = X0 + totale / 2;
  const semi = totale / 2;

  let x = X0;
  const lettere: Lettera[] = NOME.map((carattere, i) => {
    const larghezza = larghezze[i];
    const cx = x + larghezza / 2;
    const t = (cx - centro) / semi;
    const y = BASE + PROFONDITA * (1 - t * t) + (scomposto ? SCARTO_Y[i] : 0);
    // La rotazione segue la tangente: a sinistra la curva scende e le lettere
    // pendono in avanti, a destra risale e pendono all'indietro.
    const gradi =
      (Math.atan((-2 * PROFONDITA * t) / semi) * 180) / Math.PI +
      (scomposto ? SCARTO_ROT[i] : 0);
    const lettera: Lettera = {
      carattere,
      x,
      y,
      cx,
      gradi,
      larghezza,
      corpo: DIM * (scomposto ? SCALA[i] : 1),
      chiave: `${carattere}${i}`,
    };
    x += passi[i];
    return lettera;
  });

  const carrelloX = X0 + totale + 8 * CARRELLO_DIM + CARRELLO_STACCO;
  // Il bordo destro del cesto, ingrandito: «uy» ci si appoggia contro, quindi
  // la sua posizione dipende da quanto e' grande il carrello.
  const uyX = carrelloX + (5 + CESTO_LARG) * CARRELLO_DIM + UY_STACCO;
  const uyFine = uyX + UY_U_LARG - STRETTA_UY + UY_Y_LARG;

  return {
    lettere,
    centro,
    quota: lettere.at(-1)!.y,
    carrelloX,
    uyX,
    larghezza: Math.ceil(uyFine + 8),
  };
}

/* Le due composizioni si calcolano una volta sola, all'import. */
const COMPOSTA = sorriso(false);
const SCOMPOSTA = sorriso(true);

/* I tempi dell'entrata stanno tutti in `globals.css`, in un posto solo: da qui
   passa soltanto `--indice`, cioe' la posizione dell'elemento nella fila, che il
   foglio di stile non puo' sapere. Tenerne meta' qui e meta' li' vuol dire due
   linee temporali che prima o poi divergono. */
type Stile = React.CSSProperties & Record<`--${string}`, string>;

function Occhio({
  cx,
  animato,
  strizza,
}: {
  cx: number;
  animato: boolean;
  /** Quello che fa l'occhiolino a fine sequenza. */
  strizza?: boolean;
}) {
  return (
    <circle
      cx={cx}
      cy={OCCHI_Y}
      r={OCCHI_R}
      className={
        animato ? (strizza ? "marchio-occhio-strizza" : "marchio-occhio") : undefined
      }
    />
  );
}

/* La traiettoria del carrello, in scostamenti dalla posizione finale.
   **Rispecchia i fotogrammi di `marchio-arrivo` in `globals.css`**: se si cambia
   la traiettoria li', va cambiata anche qui, altrimenti la sabbia cade dove il
   carrello non passa. */
const PERCORSO = [
  { t: 0, dx: -80, dy: -28 },
  { t: 0.16, dx: -172, dy: -28 },
  { t: 0.29, dx: -161.2, dy: -1.4 },
  { t: 0.44, dx: -131.4, dy: 19 },
  { t: 0.6, dx: -89.6, dy: 28.4 },
  { t: 0.76, dx: -45.5, dy: 24.6 },
  { t: 0.89, dx: -14.9, dy: 12.1 },
  { t: 1, dx: 0, dy: 0 },
];

/** Dove si trova il carrello a una data frazione del suo viaggio. */
function lungoIlPercorso(t: number) {
  const i = Math.max(
    0,
    PERCORSO.findIndex((p, k) => k > 0 && p.t >= t) - 1,
  );
  const a = PERCORSO[i];
  const b = PERCORSO[i + 1] ?? a;
  const f = b.t === a.t ? 0 : (t - a.t) / (b.t - a.t);
  return { dx: a.dx + (b.dx - a.dx) * f, dy: a.dy + (b.dy - a.dy) * f };
}

/** I granelli lasciati dalle ruote lungo la strada. Frazioni fisse: come lo
 *  scarto delle lettere, casuali darebbero un logo diverso fra server e
 *  browser. */
const SABBIA = [
  // Si comincia a un quinto del viaggio: prima il carrello e' ancora fuori dal
  // riquadro, e un SVG in linea ritaglia quello che esce — il granello ci
  // sarebbe ma non si vedrebbe.
  { t: 0.22, r: 3.4 },
  { t: 0.34, r: 2.6 },
  { t: 0.46, r: 3.2 },
  { t: 0.58, r: 2.4 },
  { t: 0.7, r: 3.0 },
  { t: 0.82, r: 2.2 },
];

/** Il carrello, cioè la «b»: il manico è un gancio che scende in diagonale nel
 *  cesto. Estratto in un componente perché anche altrove serva il solo segno.
 *
 *  È disegnato in **coordinate proprie**, con la riga di base a zero e il
 *  manico all'origine: così la sua dimensione è un numero solo — `CARRELLO_DIM`
 *  — invece di otto costanti sparse dentro i tracciati. Lo si posa poi al suo
 *  posto con una traslazione.
 */
function Carrello({
  c,
  scomposto,
}: {
  c: ReturnType<typeof sorriso>;
  scomposto: boolean;
}) {
  const traverse = [1, 2]
    .map((i) => `M ${(5 + (i * CESTO_LARG) / 3).toFixed(1)} -22 v 20`)
    .join(" ");

  return (
    <g
      transform={`translate(${c.carrelloX.toFixed(1)},${c.quota.toFixed(1)}) scale(${CARRELLO_DIM})${
        scomposto ? ` rotate(${INCLINA_CARRELLO} ${CESTO_LARG / 2} 0)` : ""
      }`}
      stroke="currentColor"
      strokeWidth={TRATTO}
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    >
      <path d="M -8 -34 h 7 l 6 12" />
      <path d={`M 5 -22 h ${CESTO_LARG} l -5 20 h -${CESTO_LARG - 10} z`} />
      <path d={traverse} strokeWidth={TRATTO / 2} />
      <circle cx="15" cy="4" r="5.5" fill="currentColor" stroke="none" />
      <circle cx="36" cy="4" r="5.5" fill="currentColor" stroke="none" />
    </g>
  );
}

export function Logo({
  className,
  scomposto = true,
  conOcchi = true,
  animato = false,
  titolo = "Youppybuy",
}: {
  className?: string;
  /** false = versione sobria, per fattura e documenti al fornitore. */
  scomposto?: boolean;
  /** Senza occhi resta il solo sorriso, per dove l'altezza non basta. */
  conOcchi?: boolean;
  /** Entrata animata. Una sola per pagina: due marchi che volano insieme
   *  distraggono, e la seconda volta l'animazione e' solo un ritardo. */
  animato?: boolean;
  titolo?: string;
}) {
  const c = scomposto ? SCOMPOSTA : COMPOSTA;

  return (
    <svg
      viewBox={`0 0 ${c.larghezza} ${conOcchi ? ALTEZZA : ALTEZZA - 34}`}
      className={className}
      role="img"
      aria-label={titolo}
      fill="none"
    >
      <title>{titolo}</title>

      <g
        transform={conOcchi ? undefined : "translate(0,-34)"}
        fill="currentColor"
        fontWeight={scomposto ? 800 : 700}
        style={{
          fontFamily: "var(--font-marchio), var(--font-geist-sans), system-ui, sans-serif",
        }}
      >
        {conOcchi && (
          <>
            <Occhio cx={c.centro - OCCHI_DIST} animato={animato} strizza />
            <Occhio cx={c.centro + OCCHI_DIST} animato={animato} />
          </>
        )}

        <g className={animato ? "marchio-nome" : undefined}>
          {c.lettere.map(({ carattere, x, y, cx, gradi, larghezza, corpo, chiave }, i) => {
            const lettera = (
              <text
                x={x.toFixed(1)}
                y={y.toFixed(1)}
                fontSize={corpo.toFixed(1)}
                textLength={larghezza.toFixed(1)}
                lengthAdjust="spacingAndGlyphs"
                transform={`rotate(${gradi.toFixed(1)} ${cx.toFixed(1)} ${y.toFixed(1)})`}
              >
                {carattere}
              </text>
            );

            if (!animato) return <g key={chiave}>{lettera}</g>;

            /* Due gruppi annidati, e **l'ordine conta**: due trasformazioni
               annidate si compongono, la piu' esterna si applica al risultato
               della piu' interna.

               **Dentro** la curva, che ruota la lettera attorno al proprio
               centro finale e la posa distesa. **Fuori** l'entrata, che prende
               la lettera gia' distesa e la porta verso il carrello.

               Nell'ordine opposto — com'era prima — la rotazione avveniva
               attorno al centro finale mentre la lettera era gia' spostata
               verso il carrello: un perno lontano, che la scaraventava in arco
               invece di tenerla orizzontale. */
            return (
              <g
                key={chiave}
                className="marchio-entrata"
                style={
                  {
                    "--dx": `${(c.carrelloX - x).toFixed(1)}px`,
                    "--indice": String(c.lettere.length - 1 - i),
                  } as Stile
                }
              >
                <g
                  className="marchio-curva"
                  style={
                    {
                      "--ox": `${cx.toFixed(1)}px`,
                      "--oy": `${y.toFixed(1)}px`,
                      "--dy": `${(c.quota - y).toFixed(1)}px`,
                      "--rot": `${gradi.toFixed(1)}deg`,
                    } as Stile
                  }
                >
                  {lettera}
                </g>
              </g>
            );
          })}
        </g>

        {/* La sabbia sta **fuori** dal gruppo del carrello: dentro viaggerebbe
            con lui e non potrebbe restare indietro. Ogni granello si alza dove
            passa la ruota, nel momento in cui ci passa, e li' resta a
            spegnersi. E' cosi' che si fa una scia. */}
        {animato && (
          <g className="marchio-sabbia" aria-hidden>
            {SABBIA.map(({ t, r }) => {
              const p = lungoIlPercorso(t);
              return (
                <circle
                  key={t}
                  cx={(c.carrelloX + 15 * CARRELLO_DIM + p.dx).toFixed(1)}
                  cy={(c.quota + 4 * CARRELLO_DIM + p.dy).toFixed(1)}
                  r={r}
                  style={{ "--t": String(t) } as Stile}
                />
              );
            })}
          </g>
        )}

        <g className={animato ? "marchio-carrello" : undefined}>
          <Carrello c={c} scomposto={scomposto} />
        </g>

        {/* «uy» resta orizzontale: la curva è solo della faccia. Due lettere
            separate, per poterle stringere come le altre. */}
        <g className={animato ? "marchio-uy" : undefined}>
          <text
            x={c.uyX.toFixed(1)}
            y={c.quota.toFixed(1)}
            fontSize={DIM}
            textLength={UY_U_LARG}
            lengthAdjust="spacingAndGlyphs"
          >
            u
          </text>
          <text
            x={(c.uyX + UY_U_LARG - STRETTA_UY).toFixed(1)}
            y={c.quota.toFixed(1)}
            fontSize={DIM}
            textLength={UY_Y_LARG}
            lengthAdjust="spacingAndGlyphs"
          >
            y
          </text>
        </g>
      </g>
    </svg>
  );
}
